import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import {
	slowQueryGuard,
	SLOW_QUERY_DEFAULT_TIMEOUT,
} from '@confluence/query-preloader-tools/entry-points/slowQueryGuard.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import {
	parseExcludedLabels,
	resolveSpaceScope,
} from '@confluence/listlabels-macro/entry-points/listLabelsParamsUtils.ts';
import {
	useListLabelsDataQuery,
	type useListLabelsDataQuery$data,
	type useListLabelsDataQuery$variables,
} from '@confluence/listlabels-macro/entry-points/useListLabelsDataQuery.ts';
import {
	useListLabelsDataSpaceIdQuery,
	type useListLabelsDataSpaceIdQuery$data,
	type useListLabelsDataSpaceIdQuery$variables,
} from '@confluence/listlabels-macro/entry-points/useListLabelsDataSpaceIdQuery.ts';

import type {
	MacroContentNode,
	MacroPreloadContext,
	MacroPreloadHandler,
} from '../preloadMacroData.ts';

const preloadSpaceId = async (spaceKey: string): Promise<string | null> => {
	let result:
		| Awaited<
				ReturnType<
					typeof query<useListLabelsDataSpaceIdQuery$data, useListLabelsDataSpaceIdQuery$variables>
				>
		  >
		| undefined;
	await slowQueryGuard(
		'listLabelsSpaceIdPreload',
		async () => {
			result = await query<
				useListLabelsDataSpaceIdQuery$data,
				useListLabelsDataSpaceIdQuery$variables
			>({
				query: useListLabelsDataSpaceIdQuery,
				variables: { spaceKey },
				context: { single: true },
			});
		},
		SLOW_QUERY_DEFAULT_TIMEOUT,
	);
	return result?.data?.space?.id ?? null;
};

export const listLabelsPreloadHandler: MacroPreloadHandler = {
	extensionKey: 'listlabels',

	routes: ['view', 'space-overview', 'edit', 'live'],

	preload: async (node: MacroContentNode, context: MacroPreloadContext): Promise<void> => {
		try {
			const { cloudId } = getPreloaderFnContext();
			if (!cloudId) {
				return;
			}

			const macroParams = node.attrs?.parameters?.macroParams;
			const { isAllSpaces, spaceKey } = resolveSpaceScope(
				macroParams?.spaceKey?.value,
				context.spaceKey,
			);
			const excludedLabels = parseExcludedLabels(macroParams?.excludedLabels?.value);

			let spaceId: string | null = null;
			if (!isAllSpaces) {
				if (!spaceKey) {
					return;
				}
				spaceId = await preloadSpaceId(spaceKey);
				if (!spaceId) {
					return;
				}
			}

			const variables: useListLabelsDataQuery$variables = {
				cloudId,
				spaceId,
				allSpaces: isAllSpaces,
				excludedLabels,
			};

			await slowQueryGuard(
				'listLabelsPreload',
				() =>
					query<useListLabelsDataQuery$data, useListLabelsDataQuery$variables>({
						query: useListLabelsDataQuery,
						variables,
						context: { single: true },
					}),
				SLOW_QUERY_DEFAULT_TIMEOUT,
			);
		} catch {
			return;
		}
	},
};
